import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import AmbientBackground from "../components/AmbientBackground";
import ScrambleText from "../components/ScrambleText";
import { identifyLead, trackEvent } from "../lib/analytics";
import {
  getDiagnosticLifecycleStage,
  getMakdisOsEndpoint,
} from "../lib/makdisOsClient";
import {
  AnimatedSection,
  ContentPageInner,
  ContentPageShell,
  GlassPanel,
  PrimaryButton,
  SectionLead,
} from "../styles/contentPage";

const DEFAULT_FORM_VALUES = {
  firstName: "",
  lastName: "",
  company: "",
  email: "",
  phone: "",
  bottleneckArea: "",
  systems: "",
  routeFit: "",
  urgency: "",
  businessConsequence: "",
};

const isFilled = (value) => String(value || "").trim().length > 0;

const touchAllFields = () =>
  Object.keys(DEFAULT_FORM_VALUES).reduce(
    (fields, key) => ({
      ...fields,
      [key]: true,
    }),
    {}
  );

export default function ContactPage() {
  const { t, i18n } = useTranslation();
  const labels = t("contactPage.form.labels", { returnObjects: true }) || {};
  const formSections = t("contactPage.form.sections", { returnObjects: true }) || {};
  const validation = t("contactPage.form.validation", { returnObjects: true }) || {};
  const messages = t("contactPage.form.messages", { returnObjects: true }) || {};
  const bottleneckOptions =
    t("contactPage.form.bottleneckOptions", { returnObjects: true }) || [];
  const routeOptions = t("contactPage.form.routeOptions", { returnObjects: true }) || [];
  const urgencyOptions =
    t("contactPage.form.urgencyOptions", { returnObjects: true }) || [];
  const directItems = t("contactPage.direct.items", { returnObjects: true }) || [];

  const category = "diagnostic";
  const [values, setValues] = useState(DEFAULT_FORM_VALUES);
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState("idle");
  const formStartedRef = useRef(false);

  useEffect(() => {
    document.title = t("meta.title");
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", t("meta.description"));
    document.documentElement.lang = i18n.language;
  }, [i18n.language, t]);

  const isValidEmail = /.+@.+\..+/.test(values.email);
  const errors = useMemo(
    () => ({
      firstName: !isFilled(values.firstName) ? validation.required : "",
      lastName: !isFilled(values.lastName) ? validation.required : "",
      company: !isFilled(values.company) ? validation.required : "",
      email: !isValidEmail ? validation.email : "",
      phone: !isFilled(values.phone) ? validation.phone : "",
      bottleneckArea: !isFilled(values.bottleneckArea) ? validation.required : "",
      systems: !isFilled(values.systems) ? validation.required : "",
      routeFit: !isFilled(values.routeFit) ? validation.required : "",
      urgency: !isFilled(values.urgency) ? validation.required : "",
      businessConsequence: !isFilled(values.businessConsequence)
        ? validation.required
        : "",
    }),
    [
      isValidEmail,
      validation.email,
      validation.phone,
      validation.required,
      values.businessConsequence,
      values.bottleneckArea,
      values.company,
      values.firstName,
      values.lastName,
      values.phone,
      values.routeFit,
      values.systems,
      values.urgency,
    ]
  );

  const canSubmit =
    Object.values(errors).every((value) => !value) && status !== "submitting";

  const markFormStarted = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;

    trackEvent("diagnostic_form_start", {
      path: window.location.pathname,
    });
  };

  const set = (key) => (event) => {
    markFormStarted();
    setValues((current) => ({ ...current, [key]: event.target.value }));
  };

  const choose = (key, value) => {
    markFormStarted();
    setValues((current) => ({ ...current, [key]: value }));
    setTouched((current) => ({ ...current, [key]: true }));
  };

  const mark = (key) => () =>
    setTouched((current) => ({ ...current, [key]: true }));

  const submit = async (event) => {
    event.preventDefault();

    if (!canSubmit) {
      setTouched(touchAllFields());
      return;
    }

    const apiEndpoint = getMakdisOsEndpoint();

    setStatus("submitting");
    const crmLifecycleStage = getDiagnosticLifecycleStage(values);

    const payload = {
      type: "diagnostic_intake",
      category,
      conversionPoint: "diagnostic_intake",
      crmLifecycleStage,
      firstName: values.firstName,
      lastName: values.lastName,
      company: values.company,
      email: values.email,
      phone: values.phone,
      bottleneckArea: values.bottleneckArea,
      systems: values.systems,
      routeFit: values.routeFit,
      urgency: values.urgency,
      businessConsequence: values.businessConsequence,
      locale: document.documentElement.lang || "nl",
      sourcePath: window.location.pathname,
      timestamp: new Date().toISOString(),
    };

    trackEvent("diagnostic_intake_submit", {
      path: window.location.pathname,
      category,
      bottleneckArea: values.bottleneckArea,
      routeFit: values.routeFit,
      urgency: values.urgency,
    });

    try {
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Contact submit failed with status ${response.status}`);
      }

      identifyLead(values.email, {
        lead_type: "diagnostic_intake",
        route_fit: values.routeFit,
        urgency: values.urgency,
        bottleneck_area: values.bottleneckArea,
        crm_lifecycle_stage: crmLifecycleStage,
        source_path: window.location.pathname,
      });
      setStatus("success");
      setValues(DEFAULT_FORM_VALUES);
      setTouched({});
      formStartedRef.current = false;
    } catch (error) {
      console.error(error);
      setStatus("error");
    }
  };

  return (
    <Wrap className="page-container">
      <AmbientBackground />
      <Inner>
        <AnimatedSection $i={0}>
          <Hero>
            <HeroHeading>
              <ScrambleText text={t("contactPage.heading")} />
            </HeroHeading>
            <HeroLead>{t("contactPage.lead")}</HeroLead>
          </Hero>
        </AnimatedSection>

        <AnimatedSection $i={1}>
          <LayoutGrid>
            <InfoColumn>
              <InfoPanel>
                <InfoKicker>{t("contactPage.direct.title")}</InfoKicker>
                <InfoLead>{t("contactPage.direct.lead")}</InfoLead>
                <InfoList>
                  {directItems.map((item) => (
                    <InfoLink
                      key={item.label}
                      href={item.href}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noreferrer" : undefined}
                    >
                      <InfoLabel>{item.label}</InfoLabel>
                      <InfoValue>{item.value}</InfoValue>
                    </InfoLink>
                  ))}
                </InfoList>
              </InfoPanel>

              <SupportPanel>
                <InfoKicker>{t("contactPage.support.title")}</InfoKicker>
                <SupportTitle>{t("contactPage.support.heading")}</SupportTitle>
                <SupportText>{t("contactPage.support.text")}</SupportText>
              </SupportPanel>
            </InfoColumn>

            <FormPanel
              as={motion.form}
              onSubmit={submit}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
            >
              <FormSectionTitle>{formSections.contact}</FormSectionTitle>
              <FormGrid>
                <Field>
                  <FieldLabel>
                    <ScrambleText text={labels.firstName} />
                  </FieldLabel>
                  <FieldInput
                    aria-label={labels.firstName}
                    value={values.firstName}
                    onChange={set("firstName")}
                    onBlur={mark("firstName")}
                    aria-invalid={Boolean(touched.firstName && errors.firstName)}
                  />
                  <FieldError>{touched.firstName ? errors.firstName : ""}</FieldError>
                </Field>

                <Field>
                  <FieldLabel>
                    <ScrambleText text={labels.lastName} />
                  </FieldLabel>
                  <FieldInput
                    aria-label={labels.lastName}
                    value={values.lastName}
                    onChange={set("lastName")}
                    onBlur={mark("lastName")}
                    aria-invalid={Boolean(touched.lastName && errors.lastName)}
                  />
                  <FieldError>{touched.lastName ? errors.lastName : ""}</FieldError>
                </Field>

                <Field $full>
                  <FieldLabel>
                    <ScrambleText text={labels.company} />
                  </FieldLabel>
                  <FieldInput
                    aria-label={labels.company}
                    value={values.company}
                    onChange={set("company")}
                    onBlur={mark("company")}
                    aria-invalid={Boolean(touched.company && errors.company)}
                  />
                  <FieldError>{touched.company ? errors.company : ""}</FieldError>
                </Field>

                <Field>
                  <FieldLabel>
                    <ScrambleText text={labels.email} />
                  </FieldLabel>
                  <FieldInput
                    type="email"
                    aria-label={labels.email}
                    value={values.email}
                    onChange={set("email")}
                    onBlur={mark("email")}
                    aria-invalid={Boolean(touched.email && errors.email)}
                  />
                  <FieldError>{touched.email ? errors.email : ""}</FieldError>
                </Field>

                <Field>
                  <FieldLabel>
                    <ScrambleText text={labels.phone} />
                  </FieldLabel>
                  <FieldInput
                    type="tel"
                    aria-label={labels.phone}
                    value={values.phone}
                    onChange={set("phone")}
                    onBlur={mark("phone")}
                    aria-invalid={Boolean(touched.phone && errors.phone)}
                  />
                  <FieldError>{touched.phone ? errors.phone : ""}</FieldError>
                </Field>
              </FormGrid>

              <FormSectionTitle>{formSections.diagnostic}</FormSectionTitle>
              <FormGrid>
                <ChoiceField $full>
                  <FieldLabel>
                    <ScrambleText text={labels.bottleneckArea} />
                  </FieldLabel>
                  <ChoiceGroup role="radiogroup" aria-label={labels.bottleneckArea}>
                    {bottleneckOptions.map((option) => (
                      <ChoiceButton
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={values.bottleneckArea === option.value}
                        $active={values.bottleneckArea === option.value}
                        onClick={() => choose("bottleneckArea", option.value)}
                      >
                        {option.label}
                      </ChoiceButton>
                    ))}
                  </ChoiceGroup>
                  <FieldError>
                    {touched.bottleneckArea ? errors.bottleneckArea : ""}
                  </FieldError>
                </ChoiceField>

                <Field $full>
                  <FieldLabel>
                    <ScrambleText text={labels.systems} />
                  </FieldLabel>
                  <FieldTextarea
                    aria-label={labels.systems}
                    value={values.systems}
                    onChange={set("systems")}
                    onBlur={mark("systems")}
                    aria-invalid={Boolean(touched.systems && errors.systems)}
                  />
                  <FieldError>{touched.systems ? errors.systems : ""}</FieldError>
                </Field>

                <ChoiceField $full>
                  <FieldLabel>
                    <ScrambleText text={labels.routeFit} />
                  </FieldLabel>
                  <ChoiceGroup role="radiogroup" aria-label={labels.routeFit}>
                    {routeOptions.map((option) => (
                      <ChoiceButton
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={values.routeFit === option.value}
                        $active={values.routeFit === option.value}
                        onClick={() => choose("routeFit", option.value)}
                      >
                        {option.label}
                      </ChoiceButton>
                    ))}
                  </ChoiceGroup>
                  <FieldError>{touched.routeFit ? errors.routeFit : ""}</FieldError>
                </ChoiceField>

                <ChoiceField $full>
                  <FieldLabel>
                    <ScrambleText text={labels.urgency} />
                  </FieldLabel>
                  <ChoiceGroup role="radiogroup" aria-label={labels.urgency}>
                    {urgencyOptions.map((option) => (
                      <ChoiceButton
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={values.urgency === option.value}
                        $active={values.urgency === option.value}
                        onClick={() => choose("urgency", option.value)}
                      >
                        {option.label}
                      </ChoiceButton>
                    ))}
                  </ChoiceGroup>
                  <FieldError>{touched.urgency ? errors.urgency : ""}</FieldError>
                </ChoiceField>

                <Field $full>
                  <FieldLabel>
                    <ScrambleText text={labels.businessConsequence} />
                  </FieldLabel>
                  <FieldTextarea
                    aria-label={labels.businessConsequence}
                    value={values.businessConsequence}
                    onChange={set("businessConsequence")}
                    onBlur={mark("businessConsequence")}
                    aria-invalid={Boolean(
                      touched.businessConsequence && errors.businessConsequence
                    )}
                  />
                  <FieldError>
                    {touched.businessConsequence ? errors.businessConsequence : ""}
                  </FieldError>
                </Field>
              </FormGrid>

              <FormFooter>
                <FooterText>{t("contactPage.form.note")}</FooterText>
                <PrimaryButton type="submit" disabled={!canSubmit}>
                  {status === "submitting"
                    ? t("contactPage.form.submitting")
                    : t("contactPage.form.submit")}
                </PrimaryButton>
              </FormFooter>

              <StatusMessage $error={status === "error"}>
                {status === "success" ? messages.success : null}
                {status === "error" ? messages.error : null}
              </StatusMessage>
            </FormPanel>
          </LayoutGrid>
        </AnimatedSection>
      </Inner>
    </Wrap>
  );
}

const Wrap = styled(ContentPageShell)``;

const Inner = styled(ContentPageInner)``;

const Hero = styled.div`
  display: grid;
  gap: 22px;
  max-width: 58rem;
`;

const HeroHeading = styled.h1`
  margin: 0;
  font-family: var(--font-heading);
  font-size: clamp(3rem, 7vw, 6rem);
  line-height: 0.94;
  letter-spacing: -0.055em;
`;

const HeroLead = styled(SectionLead)`
  max-width: 40rem;
  margin: 0;
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 0.8fr) minmax(0, 1.2fr);
  gap: clamp(24px, 4vw, 52px);
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const InfoColumn = styled.div`
  display: grid;
  gap: 18px;
`;

const InfoPanel = styled(GlassPanel)`
  display: grid;
  gap: 18px;
  padding: clamp(22px, 3vw, 30px);
  border-radius: 30px;
`;

const SupportPanel = styled(GlassPanel)`
  display: grid;
  gap: 10px;
  padding: 24px;
  border-radius: 26px;
`;

const InfoKicker = styled.span`
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.56);
`;

const InfoLead = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.68;
  color: rgba(255, 255, 255, 0.78);
`;

const InfoList = styled.div`
  display: grid;
  gap: 12px;
`;

const InfoLink = styled.a`
  display: grid;
  gap: 4px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  color: #fff;
  text-decoration: none;
`;

const InfoLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.54);
`;

const InfoValue = styled.span`
  font-size: 20px;
  line-height: 1.28;
`;

const SupportTitle = styled.h3`
  margin: 0;
  font-size: 28px;
  line-height: 1.08;
`;

const SupportText = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.78);
`;

const FormPanel = styled(GlassPanel)`
  display: grid;
  gap: 24px;
  padding: clamp(24px, 3vw, 34px);
  border-radius: 32px;
`;

const FormSectionTitle = styled.h2`
  margin: 0 0 -4px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.56);
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 16px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: grid;
  gap: 8px;
  grid-column: ${(p) => (p.$full ? "1 / -1" : "auto")};
`;

const ChoiceField = styled.div`
  display: grid;
  gap: 8px;
  grid-column: ${(p) => (p.$full ? "1 / -1" : "auto")};
`;

const FieldLabel = styled.span`
  min-height: 20px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.74);
`;

const FieldInput = styled.input`
  width: 100%;
  min-height: 58px;
  padding: 0 18px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: #fff;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:focus {
    border-color: rgba(255, 255, 255, 0.28);
    background: rgba(255, 255, 255, 0.06);
  }
`;

const FieldTextarea = styled.textarea`
  width: 100%;
  min-height: 112px;
  padding: 16px 18px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: #fff;
  font-size: 16px;
  line-height: 1.55;
  outline: none;
  resize: vertical;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:focus {
    border-color: rgba(255, 255, 255, 0.28);
    background: rgba(255, 255, 255, 0.06);
  }
`;

const ChoiceGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const ChoiceButton = styled.button`
  min-height: 44px;
  padding: 0 15px;
  border-radius: 999px;
  border: 1px solid
    ${(p) => (p.$active ? "rgba(190, 231, 241, 0.64)" : "rgba(255, 255, 255, 0.14)")};
  background:
    ${(p) =>
      p.$active
        ? "linear-gradient(180deg, rgba(190,231,241,0.18), rgba(190,231,241,0.08))"
        : "rgba(255,255,255,0.035)"};
  color: ${(p) => (p.$active ? "#fff" : "rgba(255,255,255,0.78)")};
  font-size: 13px;
  font-weight: 760;
  letter-spacing: 0;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(190, 231, 241, 0.42);
    background: rgba(190, 231, 241, 0.08);
  }
`;

const FieldError = styled.span`
  min-height: 18px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.62);
`;

const FormFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: center;
  justify-content: space-between;
`;

const FooterText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.66);
`;

const StatusMessage = styled.p`
  margin: 0;
  min-height: 22px;
  font-size: 14px;
  line-height: 1.6;
  color: ${(p) => (p.$error ? "rgba(255, 255, 255, 0.76)" : "#fff")};
`;
